// packages/shared-utils/src/privacyDisclosure.ts
//
// The "How this site uses your data" section of a store's privacy page,
// generated from the store's live settings. Nobody maintains a vendor list by
// hand: adding a GA4 ID adds GA4 to the policy on the next render.
//
// Shared so the storefront (renders it) and the portal (previews it in the
// privacy-policy editor) can never disagree. The vendor ID patterns live here
// for the same reason: the storefront loads a tag only when its ID passes these
// checks (apps/storefront/src/lib/analyticsVendors.ts), so the policy lists
// exactly the tags that can load — never one that can't, never one it omits.

// The storefront_settings columns are plain TEXT; the portal validates on save,
// but anything that reaches a vendor URL is re-checked against these.
export const VENDOR_ID_PATTERNS = {
  ga4: /^G-[A-Z0-9]{6,12}$/,
  metaPixel: /^\d{6,20}$/,
  // Clarity project IDs are short lowercase base-36 strings (e.g. "q7rs8t9uv0").
  clarity: /^[a-z0-9]{8,15}$/,
  // GoHighLevel chat widget IDs are 24-hex object IDs (the embed's data-widget-id).
  ghlChat: /^[a-f0-9]{24}$/,
} as const

/**
 * The storefront_settings columns the disclosure reads. Every select that feeds
 * `privacyDisclosureFor` must include all of them: a missing column reads as
 * "not configured", and the policy would silently omit a tag that loads. Both
 * selects are checked against this list (storefront: a test over
 * PUBLIC_SETTINGS_SELECT; portal: the select is built from it).
 */
export const PRIVACY_DISCLOSURE_COLUMNS = [
  'cookie_consent_enabled',
  'analytics_ga4_id',
  'analytics_meta_pixel_id',
  'analytics_clarity_id',
  'ghl_chat_widget_id',
] as const

export type PrivacyDisclosureSettings = {
  cookie_consent_enabled: boolean | null
  analytics_ga4_id: string | null
  analytics_meta_pixel_id: string | null
  analytics_clarity_id: string | null
  ghl_chat_widget_id: string | null
}

export type DisclosureSection = {
  heading: string
  paragraphs: string[]
  /** Optional bullet list, rendered after the paragraphs. */
  items?: string[]
  /** Render a control that opens the visitor's privacy-choices panel. */
  opensPrivacyChoices?: boolean
}

export type PrivacyDisclosure = {
  title: string
  intro: string
  sections: DisclosureSection[]
}

export const PRIVACY_DISCLOSURE_TITLE = 'How this site uses your data'

/** Facts about the store (not storefront_settings) the disclosure needs. */
export type PrivacyDisclosureStore = {
  dealerName: string
  /** The store's public contact email, or null when it is not public. */
  contactEmail: string | null
  /** True when the store's FareHarbor booking widget loads on every page. */
  bookingWidget: boolean
  /**
   * True when the section is the whole privacy page (the dealer has no live
   * policy of their own): adds a way to make a request, and words the intro
   * as a page rather than a section.
   */
  standalone: boolean
  /**
   * The store's partner-link window in days, or null when the store has no
   * partner links (Shop tier). Names the `sf_partner` cookie (migration 428).
   */
  partnerLinkDays: number | null
}

export function privacyDisclosureFor(
  settings: Partial<PrivacyDisclosureSettings> | null | undefined,
  store: PrivacyDisclosureStore,
): PrivacyDisclosure {
  const { dealerName } = store
  const consentFirst = settings?.cookie_consent_enabled === true
  const has = (id: string | null | undefined, re: RegExp) => !!id && re.test(id)

  const analyticsTools: string[] = []
  const marketingTools: string[] = []
  if (has(settings?.analytics_ga4_id, VENDOR_ID_PATTERNS.ga4)) {
    analyticsTools.push('Google Analytics (Google): measures visits and how people use the site. Google sets its own cookies.')
  }
  if (has(settings?.analytics_meta_pixel_id, VENDOR_ID_PATTERNS.metaPixel)) {
    marketingTools.push('Meta Pixel (Meta): measures visits that come from Facebook and Instagram ads, and can build audiences for those ads. Meta sets its own cookies.')
  }
  if (has(settings?.analytics_clarity_id, VENDOR_ID_PATTERNS.clarity)) {
    marketingTools.push('Microsoft Clarity (Microsoft): records how visitors move through pages, as heatmaps and session recordings, so problems with the site can be found and fixed.')
  }

  const alwaysOn = [
    'the browser storage that keeps your cart and checkout working',
    'our payment processor, Stripe, when you check out (Stripe sets its own cookies to prevent fraud)',
    ...(store.bookingWidget ? ['FareHarbor, the booking service whose widget loads on every page so you can book'] : []),
    ...(has(settings?.ghl_chat_widget_id, VENDOR_ID_PATTERNS.ghlChat)
      ? [`the chat button, run by LeadConnector (GoHighLevel), which loads on every page so you can message ${dealerName}. What you type in it, and any name, email or phone number you give, goes to ${dealerName}. LeadConnector sets its own cookies`]
      : []),
    'Vercel Speed Insights, which times how fast pages load without identifying you',
    'reports of broken pages and errors, which record the page address but not who you are',
  ]

  const sections: DisclosureSection[] = [
    {
      heading: 'Measuring visits',
      paragraphs: [
        consentFirst
          ? 'This site asks before measuring. No analytics or marketing tool described below runs until you agree, and you can change your answer at any time.'
          : 'This site measures visits by default. You can turn this off at any time, and the site keeps working the same way.',
      ],
    },
    {
      heading: 'Our own analytics',
      paragraphs: [
        `When analytics is on, this site records how you use it: the pages you view, the products you look at, the links and buttons you click, the choices you make in the board builder or product finder, when you start a booking, quote or contact form, your device type (phone, tablet or computer), and the page that sent you here. A random visit ID, kept in your browser only for the length of the visit, groups those records together. If you send a form, it is linked to that visit so ${dealerName} can see what led to it.`,
        `This data stays with ${dealerName} and the platform that runs this site. It is not sold, and it is not used to follow you across other websites.`,
      ],
    },
    {
      heading: 'Third-party tools',
      paragraphs:
        analyticsTools.length + marketingTools.length === 0
          ? ['This site runs no third-party analytics, advertising or session-recording tools.']
          : [
              consentFirst
                ? 'These outside services run on this site. Each one loads only after you turn on the matching choice.'
                : 'These outside services run on this site. Each one loads unless you turn the matching choice off.',
            ],
      items: [
        ...analyticsTools.map((t) => `${t} Choice: Analytics.`),
        ...marketingTools.map((t) => `${t} Choice: Marketing.`),
      ],
    },
    // First-party, so not under "Third-party tools"; still a Marketing choice.
    ...(store.partnerLinkDays
      ? [
          {
            heading: 'Partner referrals',
            paragraphs: [
              `When you arrive through a partner's link, such as a hotel that recommends ${dealerName}, a cookie named sf_partner remembers that partner for up to ${store.partnerLinkDays} days so they can be credited if you book or buy. It holds no personal details. Choice: Marketing.`,
            ],
          },
        ]
      : []),
    {
      heading: 'Your choices',
      paragraphs: [
        'Use "Your Privacy Choices", at the bottom of every page, to turn Analytics or Marketing on or off. Your choice is saved on this browser for a year.',
        'If your browser sends a Global Privacy Control signal, this site treats it as an opt-out of everything above: no analytics or marketing tool runs, unless you turn a choice back on yourself.',
      ],
      opensPrivacyChoices: true,
    },
    {
      heading: 'Always on',
      paragraphs: ['A few things run whatever you choose, because the site needs them:'],
      items: alwaysOn.map((t) => t.charAt(0).toUpperCase() + t.slice(1) + '.'),
    },
  ]
  if (store.standalone) {
    sections.push({
      heading: 'Questions and requests',
      paragraphs: [
        store.contactEmail
          ? `To ask about your data, or to see, correct or delete it, email ${dealerName} at ${store.contactEmail}.`
          : `To ask about your data, or to see, correct or delete it, contact ${dealerName} through the contact details on this site.`,
      ],
    })
  }
  for (const s of sections) if (s.items?.length === 0) delete s.items

  return {
    title: PRIVACY_DISCLOSURE_TITLE,
    intro: store.standalone
      ? `This page explains what ${dealerName}'s website collects and how to control it. It is generated from the site’s live settings, so it always matches what the site actually runs.`
      : 'This section is generated from the site’s live settings, so it always matches what the site actually runs.',
    sections,
  }
}
